import { useCallback, useEffect, useRef, useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Plus } from "lucide-react";
import { isValidSharedConversationUrl } from "~/components/assistant/constants";
import type { ConversationShortcut } from "~/components/assistant/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate } from "~/utils/apiClient";
import ConversationShortcutRow from "./ConversationShortcutRow";

const { VITE_CATALOG_API } = import.meta.env;

const MAX_SHORTCUTS = 6;

const ConversationShortcutsTab = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const serverShortcuts = dataset?.conversationShortcuts ?? [];
  const serverKey = JSON.stringify(serverShortcuts);

  const [shortcuts, setShortcuts] = useState<ConversationShortcut[]>(() => serverShortcuts);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [lastServerKey, setLastServerKey] = useState(serverKey);
  const abortRef = useRef<AbortController | null>(null);

  // Sync from server on initial load or external updates
  if (serverKey !== lastServerKey) {
    setShortcuts(serverShortcuts);
    setEditingIndex(null);
    setSavingIndex(null);
    setLastServerKey(serverKey);
  }

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const saveAll = useCallback(
    async (toSave: ConversationShortcut[]): Promise<boolean> => {
      if (!dataset) return false;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await apiMutate(
          `${VITE_CATALOG_API}/datasets/${dataset.id}/conversation-shortcuts`,
          "PUT",
          { shortcuts: toSave },
          { signal: controller.signal }
        );

        setLastServerKey(JSON.stringify(toSave));
        mutate((current) => {
          if (!current || current.id !== dataset.id) return current;
          return { ...current, conversationShortcuts: toSave };
        });
        return true;
      } catch (e) {
        if ((e as Error).name === "AbortError") return false;
        console.error(e);
        showError("Unable to save conversation shortcuts");
        return false;
      }
    },
    [dataset, mutate, showError]
  );

  const handleSave = async (index: number, label: string, url: string) => {
    const updated = [...shortcuts];
    updated[index] = { label, url };
    setSavingIndex(index);

    const success = await saveAll(updated);
    setSavingIndex(null);

    if (success) {
      setShortcuts(updated);
      setEditingIndex(null);
    }
  };

  const handleCancel = (index: number) => {
    // Remove newly added empty shortcuts on cancel
    const shortcut = shortcuts[index];
    if (shortcut.label === "" && shortcut.url === "") {
      setShortcuts((prev) => prev.filter((_, i) => i !== index));
    }
    setEditingIndex(null);
  };

  const handleAdd = () => {
    if (shortcuts.length >= MAX_SHORTCUTS) return;
    const updated = [...shortcuts, { url: "", label: "" }];
    setShortcuts(updated);
    setEditingIndex(updated.length - 1);
  };

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (deleteIndex === null || !dataset) return;

    const updated = shortcuts.filter((_, i) => i !== deleteIndex);

    setDeleting(true);
    const success = await saveAll(updated);
    if (success) {
      setShortcuts(updated);
      if (editingIndex === deleteIndex) {
        setEditingIndex(null);
      } else if (editingIndex !== null && editingIndex > deleteIndex) {
        setEditingIndex(editingIndex - 1);
      }
    }
    setDeleting(false);
    setDeleteIndex(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Pin up to 6 featured conversation links that all users will see in the conversation history.
        URLs must point to shared conversations.
      </p>
      <div className="divide-y divide-border">
        {shortcuts.map((shortcut, index) => (
          <ConversationShortcutRow
            key={`${index}-${editingIndex === index}`}
            index={index}
            label={shortcut.label}
            url={shortcut.url}
            editing={editingIndex === index}
            saving={savingIndex === index}
            disabled={!isOwner}
            validateUrl={isValidSharedConversationUrl}
            onEdit={() => setEditingIndex(index)}
            onSave={(label, url) => handleSave(index, label, url)}
            onCancel={() => handleCancel(index)}
            onDelete={() => setDeleteIndex(index)}
          />
        ))}
      </div>
      {isOwner && shortcuts.length < MAX_SHORTCUTS && editingIndex === null && (
        <div>
          <Button variant="secondary" size="sm" onClick={handleAdd}>
            <Plus className="size-4 mr-1" />
            Add Shortcut
          </Button>
        </div>
      )}
      <AlertDialog
        open={deleteIndex !== null}
        onOpenChange={(open) => !deleting && !open && setDeleteIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shortcut</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this conversation shortcut?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} onClick={() => setDeleteIndex(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="size-4 mr-1 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConversationShortcutsTab;
